// @flow

import {ActionsObservable, ofType} from 'redux-observable';
import {
  getProductFiltersSettingApi,
  saveProductFiltersSettingApi,
  getProductSearchSettingApi,
  saveProductSearchSettingApi,
  getVectorizeStatusApi,
} from './api';
import {map, switchMap} from 'rxjs/operators';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';

import type {ProductFilterSetting} from './entities';
import type {Action} from '../../store/utils';
import type {
  ProductFilterSettingRequest,
  ProductSearchSettingResponse,
  VectorizeStatusResponse,
} from './api';

export type ProductFilterAction = Action & {
  error?: string,
  productFilterSettingRequest?: ProductFilterSettingRequest,
  searchSettingRequest?: {enabled: boolean},
};

export type ProductFiltersState = {
  isFetching?: boolean,
  error?: string,
  savingSuccess?: boolean,
  productFiltersSetting?: ProductFilterSetting,
  isFetchingSearch?: boolean,
  isSavingSearch?: boolean,
  searchError?: string,
  productSearchSetting?: ProductSearchSettingResponse,
  isFetchingVectorizeStatus?: boolean,
  vectorizeStatus?: VectorizeStatusResponse,
  vectorizeStatusError?: string,
};

export class FetchProductFilterSetting extends ActionCreator<ProductFilterAction> {}
export class FetchProductFilterSettingSuccess extends ActionCreator<ProductFilterAction> {}
export class FetchProductFilterSettingError extends ActionCreator<ProductFilterAction> {}

export class SaveProductFilterSetting extends ActionCreator<ProductFilterAction> {}
export class SaveProductFilterSettingSuccess extends ActionCreator<ProductFilterAction> {}
export class SaveProductFilterSettingError extends ActionCreator<ProductFilterAction> {}

export class FetchProductSearchSetting extends ActionCreator<ProductFilterAction> {}
export class FetchProductSearchSettingSuccess extends ActionCreator<ProductFilterAction> {}
export class FetchProductSearchSettingError extends ActionCreator<ProductFilterAction> {}

export class SaveProductSearchSetting extends ActionCreator<ProductFilterAction> {}
export class SaveProductSearchSettingSuccess extends ActionCreator<ProductFilterAction> {}
export class SaveProductSearchSettingError extends ActionCreator<ProductFilterAction> {}

export class FetchVectorizeStatus extends ActionCreator<ProductFilterAction> {}
export class FetchVectorizeStatusSuccess extends ActionCreator<ProductFilterAction> {}
export class FetchVectorizeStatusError extends ActionCreator<ProductFilterAction> {}

export const actions = {
  fetchProductFiltersSetting: () => new FetchProductFilterSetting(),
  saveProductFilterSetting: (payload: ProductFilterAction) =>
    new SaveProductFilterSetting(payload),
  fetchProductSearchSetting: () => new FetchProductSearchSetting(),
  saveProductSearchSetting: (payload: ProductFilterAction) =>
    new SaveProductSearchSetting(payload),
  fetchVectorizeStatus: () => new FetchVectorizeStatus(),
};

export const initialState = {};

export default createReducer(initialState, {
  [FetchProductFilterSetting.type]: _ => ({isFetching: true}),
  [FetchProductFilterSettingSuccess.type]: (
    state: ProductFiltersState,
    action: ProductFilterAction
  ) => ({isFetching: false, productFiltersSetting: action.data}),
  [FetchProductFilterSettingError.type]: (_, action: ProductFilterAction) => ({
    isFetching: false,
    error: action.error,
  }),
  // Save product filter setting
  [SaveProductFilterSetting.type]: _ => ({isFetching: true}),
  [SaveProductFilterSettingSuccess.type]: (
    state: ProductFiltersState,
    action: ProductFilterAction
  ) => ({isFetching: false, productFiltersSetting: action.data}),
  [SaveProductFilterSettingError.type]: (_, action: ProductFilterAction) => ({
    isFetching: false,
    error: action.error,
  }),
  // Fetch product search setting
  [FetchProductSearchSetting.type]: (state: ProductFiltersState) => ({
    ...state,
    isFetchingSearch: true,
    searchError: undefined,
  }),
  [FetchProductSearchSettingSuccess.type]: (
    state: ProductFiltersState,
    action: ProductFilterAction
  ) => ({...state, isFetchingSearch: false, productSearchSetting: action.data}),
  [FetchProductSearchSettingError.type]: (
    state: ProductFiltersState,
    action: ProductFilterAction
  ) => ({...state, isFetchingSearch: false, searchError: action.error}),
  // Save product search setting
  [SaveProductSearchSetting.type]: (state: ProductFiltersState) => ({
    ...state,
    isSavingSearch: true,
    searchError: undefined,
  }),
  [SaveProductSearchSettingSuccess.type]: (
    state: ProductFiltersState,
    action: ProductFilterAction
  ) => ({...state, isSavingSearch: false, productSearchSetting: action.data}),
  [SaveProductSearchSettingError.type]: (
    state: ProductFiltersState,
    action: ProductFilterAction
  ) => ({...state, isSavingSearch: false, searchError: action.error}),
  // Fetch vectorize status
  [FetchVectorizeStatus.type]: (state: ProductFiltersState) => ({
    ...state,
    isFetchingVectorizeStatus: true,
    vectorizeStatusError: undefined,
  }),
  [FetchVectorizeStatusSuccess.type]: (
    state: ProductFiltersState,
    action: ProductFilterAction
  ) => ({
    ...state,
    isFetchingVectorizeStatus: false,
    vectorizeStatus: action.data,
  }),
  [FetchVectorizeStatusError.type]: (
    state: ProductFiltersState,
    action: ProductFilterAction
  ) => ({
    ...state,
    isFetchingVectorizeStatus: false,
    vectorizeStatusError: action.error,
  }),
});

export function fetchProductFiltersSettingEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(
      FetchProductFilterSetting.type,
      SaveProductFilterSettingSuccess.type
    ),
    switchMap(() =>
      getProductFiltersSettingApi().pipe(
        map(
          (data: ProductFilterSetting) =>
            new FetchProductFilterSettingSuccess({data})
        ),
        buildCatchError(FetchProductFilterSettingError)
      )
    )
  );
}

export function saveProductFiltersSettingEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveProductFilterSetting.type),
    switchMap(({productFilterSettingRequest}: ProductFilterAction) =>
      saveProductFiltersSettingApi(productFilterSettingRequest).pipe(
        map(
          (data: ProductFilterSetting) =>
            new SaveProductFilterSettingSuccess({data})
        ),
        buildCatchError(SaveProductFilterSettingError)
      )
    )
  );
}

export function fetchProductSearchSettingEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchProductSearchSetting.type),
    switchMap(() =>
      getProductSearchSettingApi().pipe(
        map(
          (data: ProductSearchSettingResponse) =>
            new FetchProductSearchSettingSuccess({data})
        ),
        buildCatchError(FetchProductSearchSettingError)
      )
    )
  );
}

export function saveProductSearchSettingEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveProductSearchSetting.type),
    switchMap(({searchSettingRequest}: ProductFilterAction) =>
      saveProductSearchSettingApi(searchSettingRequest).pipe(
        map(
          (data: ProductSearchSettingResponse) =>
            new SaveProductSearchSettingSuccess({data})
        ),
        buildCatchError(SaveProductSearchSettingError)
      )
    )
  );
}

export function fetchVectorizeStatusEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchVectorizeStatus.type),
    switchMap(() =>
      getVectorizeStatusApi().pipe(
        map(
          (data: VectorizeStatusResponse) =>
            new FetchVectorizeStatusSuccess({data})
        ),
        buildCatchError(FetchVectorizeStatusError)
      )
    )
  );
}
